import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { todayInWarsaw } from '../events/calendar';
import { useUrlState } from '../nav/urlState';
import { formatDue, isOverdue } from '../company-tasks/types';
import { NewPrivateTaskDialog, PrivateTaskDialog, type NewTaskStart } from './PrivateTaskDialog';
import { parseQuickLine, whenText } from './quickLine';
import { EMPTY_TEXT, isView, linkText, PRIVATE_BASE, PRIVATE_KEY, VIEWS, type ListAnswer, type PrivateTask, type View } from './types';
import '../company-tasks/tasks.css';
import './private.css';
import { useUnsavedGuard } from '../ui/unsaved';

/**
 * "Moje zadania" (owner 2026-10-22 01:17/01:19): the user's own organizer, seen by nobody else. One line to add,
 * the date views, ticking off in the row, everything else in the task's dialog (`?zadanie=<id>`, also the reminder's
 * link). The view and the open task live in the address (`lista`, `zadanie`).
 */
export function PrivateTasks() {
  const queryClient = useQueryClient();
  const { params, update } = useUrlState();
  const listParam = params.get('lista');
  const view: View = isView(listParam) ? listParam : 'today';
  const openId = params.get('zadanie');
  const today = todayInWarsaw();
  const list = useQuery({
    queryKey: [...PRIVATE_KEY, 'list', view],
    placeholderData: keepPreviousData,
    queryFn: () => api<ListAnswer>(`${PRIVATE_BASE}?view=${view}`),
  });
  const tick = useMutation({
    mutationFn: (t: PrivateTask) => api<PrivateTask>(`${PRIVATE_BASE}/${t.id}`, { method: 'PATCH', body: { version: t.version, done: !t.done } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PRIVATE_KEY }),
  });
  // the box changes at once (as in "Zadania firmowe"); the list follows the server's answer
  const toggle = (t: PrivateTask) => {
    void queryClient.cancelQueries({ queryKey: [...PRIVATE_KEY, 'list'] });
    queryClient.setQueriesData<ListAnswer>({ queryKey: [...PRIVATE_KEY, 'list'] }, (data) => (data ? { ...data, tasks: data.tasks.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)) } : data));
    tick.mutate(t);
  };
  const tasks = list.data?.tasks ?? [];
  const counts = list.data?.counts;
  const error = list.error ?? tick.error;

  return (
    <div data-feature-id="private-tasks.list">
      <p className="lede">Twój prywatny organizer. Nikt inny w aplikacji nie widzi Twoich zadań prywatnych.</p>
      <QuickAdd defaultDay={view === 'today' ? today : null} />
      <div className="ct-filters">
        <div className="segment pt-views" role="group" aria-label="Pokaż">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" aria-pressed={view === v.id} onClick={() => update({ lista: v.id === 'today' ? null : v.id })}>
              {v.label}
              {v.id === 'today' && counts && counts.today > 0 && <span className="pt-count"> {counts.today}</span>}
            </button>
          ))}
        </div>
      </div>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {error && <p className="alert" role="alert">{error instanceof ApiError ? error.message : 'Nie udało się wczytać zadań'}</p>}
      {list.data && tasks.length === 0 && <p className="muted">{EMPTY_TEXT[view]}</p>}
      {tasks.length > 0 && (
        <div className="table-wrap">
          <table className="table ct-table" aria-label="Moje zadania">
            <thead>
              <tr>
                <th scope="col" className="ct-check-col">
                  <span className="visually-hidden">Zrobione</span>
                </th>
                <th scope="col">Zadanie</th>
                <th scope="col">Termin</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => {
                const overdue = !!t.dueDay && isOverdue({ done: t.done, dueDay: t.dueDay }, today);
                const link = linkText(t);
                return (
                  <tr key={t.id} className={t.done ? 'ct-is-done' : undefined}>
                    <td className="ct-check-col">
                      <input type="checkbox" aria-label={`Zrobione: ${t.title}`} checked={t.done} disabled={tick.isPending} onChange={() => toggle(t)} />
                    </td>
                    <th scope="row">
                      <button type="button" className="ct-title" onClick={() => update({ zadanie: t.id })}>
                        {t.title}
                      </button>
                      {t.repeat && (
                        <span className="ct-repeat" title={t.repeat === 'weekly' ? 'Powtarza się co tydzień' : 'Powtarza się co miesiąc'}>
                          {' '}
                          ↻
                        </span>
                      )}
                      {link && <span className="pt-link">{link}</span>}
                    </th>
                    <td className={overdue ? 'ct-overdue' : undefined}>
                      {t.dueDay ? formatDue({ dueDay: t.dueDay, dueTime: t.dueTime }) : '—'}
                      {overdue && <span className="visually-hidden"> (zaległe)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {openId && <PrivateTaskDialog taskId={openId} onClose={() => update({ zadanie: null })} />}
    </div>
  );
}

/**
 * One line: "Zadzwonić do Kowalskiego jutro 10:00". Under it, how the date was read. Enter (or "Dodaj") opens the new
 * task's dialog with it (owner 2026-11-01); a second Enter there adds it, Esc leaves the line as it was.
 */
function QuickAdd({ defaultDay }: { defaultDay: string | null }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [start, setStart] = useState<NewTaskStart | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const today = todayInWarsaw();
  const parsed = parseQuickLine(text, today);
  const dueDay = parsed.dueDay ?? defaultDay;
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<PrivateTask>(PRIVATE_BASE, { method: 'POST', body }),
    onSuccess: async () => {
      setText('');
      input.current?.focus();
      await queryClient.invalidateQueries({ queryKey: PRIVATE_KEY });
    },
  });
  const issues = add.error instanceof ApiError ? fieldIssues(add.error) : [];
  const body = () => ({ title: parsed.title, ...(dueDay ? { dueDay } : {}), ...(parsed.dueTime ? { dueTime: parsed.dueTime } : {}) });
  // owner 2026-10-23 21:14: a task typed and not added: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: text.trim() !== '',
    save: async () => {
      if (!parsed.title) return false;
      await add.mutateAsync(body());
      return true;
    },
  });
  return (
    <>
      <form
        ref={guardRef}
        className="ct-quick pt-quick"
        aria-label="Szybkie dodawanie mojego zadania"
        onSubmit={(e) => {
          e.preventDefault();
          if (!parsed.title) return;
          setStart({ title: parsed.title, dueDay, dueTime: parsed.dueTime });
        }}
      >
        <input
          ref={input}
          className="ct-quick-title"
          aria-label="Nowe zadanie"
          aria-describedby="pt-quick-when"
          placeholder="Nowe zadanie, np. Zadzwonić do Kowalskiego jutro 10:00"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={240}
          autoComplete="off"
        />
        <button className="btn primary" type="submit" disabled={add.isPending || !parsed.title}>
          Dodaj
        </button>
        <span id="pt-quick-when" className="pt-when muted" aria-live="polite">
          {text.trim() ? `→ ${whenText(dueDay, parsed.dueTime, today)}` : ''}
        </span>
        {add.error && (
          <p className="alert ct-quick-error" role="alert">
            {issues.length > 0 ? issues.map((i) => i.message).join('. ') : add.error instanceof ApiError ? add.error.message : 'Nie udało się dodać zadania'}
          </p>
        )}
      </form>
      {start && (
        <NewPrivateTaskDialog
          start={start}
          onClose={() => setStart(null)}
          onCreated={() => {
            setStart(null);
            setText('');
            add.reset();
            // after the dialog is gone (its own focus return finds the focus taken and leaves it)
            setTimeout(() => input.current?.focus());
          }}
        />
      )}
    </>
  );
}
